import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './data-table.style.js';
import {
  DataTableConfig,
  ColumnDefinition,
  FilterDefinition,
  ActionDefinition,
  SortDirection,
  ColumnKey
} from './data-table.types.js';

@customElement('data-table')
export class DataTable<T = any> extends LitElement {
  @property({ type: Object }) config!: DataTableConfig<T>;

  @state() private searchTerm = '';
  @state() private filterValues: Record<string, string> = {};
  @state() private sortKey: string = '';
  @state() private sortDir: SortDirection = 'asc';
  @state() private currentPage = 1;
  @state() private pageSize = 5;
  @state() private columnOrder: string[] = [];

  private dragColKey?: string;
  private searchTimer?: number;
  private resizeObserver?: ResizeObserver;
  private userSetPageSize = false; // Track if user manually set page size

  connectedCallback() {
    super.connectedCallback();
    this.initializeDefaults();
    // Set initial page size as user preference to prevent dynamic override
    this.userSetPageSize = true;
  }

  firstUpdated() {
    this.setupResizeObserver();
    this.setupColumnOrder();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.recalcPageSize);
  }

  private initializeDefaults() {
    // Set default sort if provided
    if (this.config.defaultSort) {
      this.sortKey = this.config.defaultSort.key;
      this.sortDir = this.config.defaultSort.direction;
    }

    // Initialize filter values
    this.config.filters?.forEach(filter => {
      this.filterValues[filter.key] = '';
    });

    // Apply default filters if provided
    if (this.config.defaultFilters) {
      Object.entries(this.config.defaultFilters).forEach(([key, value]) => {
        this.filterValues[key] = value;
      });
    }
  }

  private setupColumnOrder() {
    this.columnOrder = this.config.columns.map(col => String(col.key));
  }

  private setupResizeObserver() {
    if (!this.config.pagination?.dynamic) return;

    const headerEl = this.renderRoot.querySelector('.header') as HTMLElement | null;
    const setVar = () => {
      const h = Math.ceil(headerEl?.getBoundingClientRect().height || 0);
      (this.style as any).setProperty('--header-h', `${h}px`);
      this.recalcPageSize();
    };
    setVar();
    if (headerEl) new ResizeObserver(setVar).observe(headerEl);

    const container = this.renderRoot.querySelector('.table-container') as HTMLElement | null;
    if (container) {
      this.resizeObserver = new ResizeObserver(() => this.recalcPageSize());
      this.resizeObserver.observe(container);
    }
    window.addEventListener('resize', this.recalcPageSize);
  }

  private recalcPageSize = () => {
    if (!this.config.pagination?.dynamic || this.userSetPageSize) return;
    
    const container = this.renderRoot.querySelector('.table-container') as HTMLElement | null;
    const thead = this.renderRoot.querySelector('thead') as HTMLElement | null;
    if (!container) return;
    
    const containerH = container.clientHeight;
    const headerH = thead ? thead.getBoundingClientRect().height : 40;
    const rowH = 50;
    const available = Math.max(0, containerH - headerH);
    const rows = Math.max(5, Math.floor(available / rowH));
    
    if (rows !== this.pageSize) { 
      this.pageSize = rows; 
      this.currentPage = 1; 
    }
  };

  private getValue(item: T, key: string): any {
    // Handle nested properties like 'model.status_cat'
    if (key.includes('.')) {
      const keys = key.split('.');
      let value = item;
      for (const k of keys) {
        value = (value as any)?.[k];
        if (value === undefined || value === null) break;
      }
      return value;
    }
    
    // Try nested access first (for work orders with model structure)
    const nestedValue = (item as any)?.model?.[key];
    if (nestedValue !== undefined && nestedValue !== null) {
      return nestedValue;
    }
    
    // Fall back to direct access (for other data structures)
    return (item as any)[key];
  }

  private compare(a: any, b: any) {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (typeof a === 'string' && typeof b === 'string') 
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    return a > b ? 1 : a < b ? -1 : 0;
  }

  private get filteredAndSorted(): T[] {
    const dataArray = Array.isArray(this.config.data) ? this.config.data : [];
    const term = this.searchTerm.trim().toLowerCase();

    // Apply filters
    const filtered = dataArray.filter(item => {
      // Apply dropdown filters
      for (const [key, value] of Object.entries(this.filterValues)) {
        if (value) {
          const itemValue = this.getValue(item, key);
          // Handle string/number comparison for filters
          if (String(itemValue) !== String(value)) {
            return false;
          }
        }
      }

      // Apply search filter
      if (!term) return true;

      // Search across all searchable columns (text, custom, and untyped)
      const searchableText = this.config.columns
        .filter(col => col.type === 'text' || col.type === 'custom' || !col.type)
        .map(col => {
          const value = this.getValue(item, String(col.key));
          return String(value || '').toLowerCase();
        })
        .join(' ');

      return searchableText.includes(term);
    });

    // Apply sorting
    if (!this.sortKey) return filtered;

    const sortColumn = this.config.columns.find(col => String(col.key) === this.sortKey);
    if (!sortColumn) return filtered;

    return [...filtered].sort((a, b) => {
      const aVal = this.getValue(a, this.sortKey);
      const bVal = this.getValue(b, this.sortKey);
      const comparison = this.compare(aVal, bVal);
      return this.sortDir === 'asc' ? comparison : -comparison;
    });
  }

  private get pagedRows(): T[] {
    if (!this.config.pagination?.enabled) return this.filteredAndSorted;
    
    const rows = this.filteredAndSorted;
    const start = (this.currentPage - 1) * this.pageSize;
    return rows.slice(start, start + this.pageSize);
  }

  private handleSearchChange = (ev: Event) => {
    const value = (ev.target as HTMLInputElement).value;
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => {
      this.searchTerm = value;
      this.currentPage = 1;
      this.config.onFilterChange?.({ search: value, ...this.filterValues });
    }, 250);
  };

  private handleFilterChange = (filterKey: string) => (ev: CustomEvent) => {
    const value = (ev.target as any).value || '';
    this.filterValues = { ...this.filterValues, [filterKey]: value };
    this.currentPage = 1;
    this.config.onFilterChange?.({ search: this.searchTerm, ...this.filterValues });
  };

  private clearFilters = () => {
    this.searchTerm = '';
    this.filterValues = {};
    this.currentPage = 1;

    // Clear the actual input elements
    const searchInput = this.renderRoot.querySelector('#search-input') as any;
    if (searchInput) searchInput.value = '';

    this.config.filters?.forEach(filter => {
      const filterSelect = this.renderRoot.querySelector(`#filter-${filter.key}`) as any;
      if (filterSelect) filterSelect.value = '';
    });

    this.config.onFilterChange?.({ search: '', ...this.filterValues });
  };

  private hasActiveFilters(): boolean {
    return !!(this.searchTerm || Object.values(this.filterValues).some(v => v));
  }

  private toggleSort = (columnKey: string) => {
    const column = this.config.columns.find(col => String(col.key) === columnKey);
    if (!column?.sortable) return;

    if (this.sortKey === columnKey) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = columnKey;
      this.sortDir = 'asc';
    }

    this.config.onSort?.(columnKey, this.sortDir);
  };

  private getAriaSort(columnKey: string) {
    if (this.sortKey !== columnKey) return 'none';
    return this.sortDir === 'asc' ? 'ascending' : 'descending';
  }

  private handleRowClick = (e: Event, item: T) => {
    if (!this.config.clickableRows) return;
    
    const target = e.target as HTMLElement;
    if (target.closest('sl-button') || target.closest('.row-actions')) {
      return;
    }
    
    this.config.onRowClick?.(item);
  };


  private handlePageChange = (e: CustomEvent) => {
    const page = (e as any).detail?.page ?? 1;
    this.currentPage = page;
  };

  private handlePageSizeChange = (e: CustomEvent) => {
    const newPageSize = parseInt((e.target as any).value) || 10;
    this.pageSize = newPageSize;
    this.currentPage = 1; // Reset to first page when changing page size
    this.userSetPageSize = true; // Mark that user has manually set page size
  };

  private goToPage = (page: number) => {
    if (page >= 1 && page <= Math.ceil(this.filteredAndSorted.length / this.pageSize)) {
      this.currentPage = page;
    }
  };

  private onThDragStart = (e: DragEvent, colKey: string) => {
    this.dragColKey = colKey;
    e.dataTransfer!.effectAllowed = 'move';
    e.dataTransfer!.setData('text/plain', colKey);
  };

  private onThDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer!.dropEffect = 'move';
    
    // Add visual feedback
    const target = e.currentTarget as HTMLElement;
    target.classList.add('drag-over');
  };

  private onThDragLeave = (e: DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
  };

  private onThDrop = (e: DragEvent, targetColKey: string) => {
    e.preventDefault();
    
    // Remove visual feedback
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
    
    if (!this.dragColKey || this.dragColKey === targetColKey) {
      this.dragColKey = undefined;
      return;
    }

    const newOrder = [...this.columnOrder];
    const dragIndex = newOrder.indexOf(this.dragColKey);
    const targetIndex = newOrder.indexOf(targetColKey);

    if (dragIndex !== -1 && targetIndex !== -1) {
      // Remove the dragged column
      const [draggedCol] = newOrder.splice(dragIndex, 1);
      // Insert it at the target position
      newOrder.splice(targetIndex, 0, draggedCol);
      this.columnOrder = newOrder;
    }

    this.dragColKey = undefined;
  };

  private renderCell(column: ColumnDefinition<T>, item: T) {
    const value = this.getValue(item, String(column.key));
    const cellClass = column.className || `${String(column.key)}-cell`;

    switch (column.type) {
      case 'custom':
        return html`<td class=${cellClass}>${column.renderer?.(value, item)}</td>`;
      
      case 'badge':
        const variant = column.badgeVariant?.(value, item) || 'neutral';
        const label = column.badgeLabel?.(value, item) || String(value || '');
        return html`<td class=${cellClass}><sl-badge pill variant=${variant}>${label}</sl-badge></td>`;
      
      case 'date':
        const formattedDate = column.formatter?.(value, item) || this.formatDate(value);
        return html`<td class=${cellClass}>${formattedDate}</td>`;
      
      default:
        let displayValue = column.formatter?.(value, item) || String(value || '');
        if (column.truncate && displayValue.length > column.truncate) {
          displayValue = displayValue.slice(0, column.truncate - 1) + '…';
        }
        return html`<td class=${cellClass}>${displayValue}</td>`;
    }
  }

  private formatDate(ts?: number): string {
    if (!ts || Number.isNaN(ts)) return '—';
    try {
      const d = new Date(ts);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch { 
      return '—'; 
    }
  }

  private renderHeader() {
    const filteredData = this.filteredAndSorted;
    
    return html`
      <div class="header">
        <div class="filters">
          <sl-input 
            id="search-input"
            placeholder=${this.config.searchPlaceholder || "Search..."}
            size="small" 
            autocomplete="off" 
            value=${this.searchTerm}
            @sl-input=${this.handleSearchChange}>
            <sl-icon slot="prefix" name="search"></sl-icon>
          </sl-input>

          ${(this.config.filters || []).map(filter => html`
            <sl-select 
              id="filter-${filter.key}"
              hoist 
              size=${filter.size || 'small'} 
              value=${this.filterValues[filter.key] || ''}
              @sl-change=${this.handleFilterChange(filter.key)}>
              <sl-option value="">${filter.placeholder || `All ${filter.label}`}</sl-option>
              ${(filter.options || []).map(option => html`
                <sl-option value=${option.value}>${option.label}</sl-option>
              `)}
            </sl-select>
          `)}
        </div>

        <div class="title-group">
          ${this.config.title ? html`
            <div class="title">
              <h3 style="margin:0;">${this.config.title}</h3>
              <sl-badge pill>${filteredData.length} of ${this.config.data.length}</sl-badge>
            </div>
          ` : null}
          
          <sl-button 
            ?disabled=${!this.hasActiveFilters()} 
            size="medium" 
            @click=${this.clearFilters}>
            <sl-icon name="x" slot="prefix"></sl-icon>
            Clear filters
          </sl-button>
          
          ${(this.config.headerActions || []).map(action => html`
            <sl-button 
              variant=${action.variant || 'primary'} 
              size=${action.size || 'medium'}
              @click=${(e: Event) => { 
                e.stopPropagation();
                action.onClick(undefined, e); 
                this.config.onAction?.(action.key); 
              }}>
              ${action.icon ? html`<sl-icon name=${action.icon} slot="prefix"></sl-icon>` : null}
              ${action.label}
            </sl-button>
          `)}
        </div>
      </div>
    `;
  }

  private renderTable() {
    const allRows = this.filteredAndSorted;
    const rows = this.pagedRows;

    return html`
      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              ${this.columnOrder.map(colKey => {
                const column = this.config.columns.find(c => String(c.key) === colKey);
                if (!column) return null;
                
                const sortable = column.sortable;
                const aria = this.getAriaSort(colKey);
                const extraClass = column.className || '';
                
                return html`
                  <th class=${extraClass}
                      role=${sortable ? 'button' : 'columnheader'}
                      aria-sort=${aria}
                      draggable="true"
                      @click=${sortable ? () => this.toggleSort(colKey) : null}
                      @dragstart=${(e: DragEvent) => this.onThDragStart(e, colKey)}
                      @dragover=${this.onThDragOver}
                      @dragleave=${this.onThDragLeave}
                      @drop=${(e: DragEvent) => this.onThDrop(e, colKey)}
                      style=${column.width ? `width: ${column.width}` : ''}>
                    <div class="column-header">
                      <span>${column.label}</span>
                      ${sortable ? html`
                        <sl-icon name=${
                          (this.sortKey === colKey)
                            ? (this.sortDir === 'asc' ? 'chevron-up' : 'chevron-down')
                            : 'chevron-expand'
                        }></sl-icon>
                      ` : null}
                    </div>
                  </th>
                `;
              })}
            </tr>
          </thead>

          <tbody>
            ${this.config.loading ? html`
              <tr>
                <td colspan=${this.config.columns.length} class="loading-state">
                  <sl-spinner></sl-spinner>
                </td>
              </tr>
            ` : rows.length === 0 ? html`
              <tr>
                <td colspan=${this.config.columns.length} class="empty-state">
                  <div class="empty">
                    ${this.config.emptyState?.icon ? html`<sl-icon name=${this.config.emptyState.icon}></sl-icon>` : null}
                    <p>${this.config.emptyState?.message || 'No data found.'}</p>
                  </div>
                </td>
              </tr>
            ` : rows.map(item => html`
              <tr tabindex="0" 
                  style=${this.config.clickableRows ? 'cursor: pointer;' : ''}
                  @click=${(e: Event) => this.handleRowClick(e, item)}
                  @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.config.clickableRows && this.config.onRowClick?.(item)}>
                ${this.columnOrder.map(colKey => {
                  const column = this.config.columns.find(c => String(c.key) === colKey);
                  return column ? this.renderCell(column, item) : null;
                })}
              </tr>
            `)}
          </tbody>
        </table>

        ${!this.config.loading && this.config.pagination?.enabled ? html`
          <div class="pagination-container">
            <div class="pagination-controls">
              <div class="rows-per-page">
                <span>Rows per page:</span>
                <sl-select 
                  size="small" 
                  value=${this.pageSize.toString()}
                  @sl-change=${this.handlePageSizeChange}>
                  <sl-option value="5">5</sl-option>
                  <sl-option value="10">10</sl-option>
                  <sl-option value="25">25</sl-option>
                  <sl-option value="50">50</sl-option>
                  <sl-option value="100">100</sl-option>
                </sl-select>
              </div>
              
              <span class="pagination-text">
                ${((this.currentPage - 1) * this.pageSize) + 1}–${Math.min(this.currentPage * this.pageSize, allRows.length)} of ${allRows.length}
              </span>
              
              ${allRows.length > this.pageSize ? html`
                <div class="pagination-nav">
                  <sl-button 
                    size="small" 
                    variant="default"
                    ?disabled=${this.currentPage === 1}
                    @click=${() => this.goToPage(this.currentPage - 1)}>
                    <sl-icon name="chevron-left"></sl-icon>
                  </sl-button>
                  <sl-button 
                    size="small" 
                    variant="default"
                    ?disabled=${this.currentPage >= Math.ceil(allRows.length / this.pageSize)}
                    @click=${() => this.goToPage(this.currentPage + 1)}>
                    <sl-icon name="chevron-right"></sl-icon>
                  </sl-button>
                </div>
              ` : null}
            </div>
          </div>
        ` : null}
      </div>
    `;
  }

  render() {
    if (this.config.error) {
      return html`<sl-alert variant="danger" open>${this.config.error}</sl-alert>`;
    }

    return html`
      <div class="data-table-wrapper">
        ${this.renderHeader()}
        ${this.renderTable()}
      </div>
    `;
  }

  static styles = styles;
}

declare global {
  interface HTMLElementTagNameMap {
    'data-table': DataTable;
  }
}
